import { useState } from "react";
import { ImageIcon, X } from "lucide-react";
import { toast } from "@ndd/ui";
import { cn } from "@/lib/utils";
import type { ObservationImageData } from "../../../../shared/observation-types";
import {
  formatObservationImageByteSize,
  observationImagePath,
  readApiError,
} from "@/lib/observation-image-client";
import { ObservationImageCarouselDialog } from "./observation-image-carousel-dialog";
import { ObservationImageDeleteDialog } from "./observation-image-delete-dialog";

interface ObservationImageGalleryProps {
  rdId: string;
  noteId: string;
  images: ObservationImageData[];
  editable?: boolean;
  heading?: string;
  className?: string;
  onChanged?: (images: ObservationImageData[]) => void;
}

export function ObservationImageGallery({
  rdId,
  noteId,
  images,
  editable = false,
  heading = "Images",
  className,
  onChanged,
}: ObservationImageGalleryProps) {
  const [deletedImageIds, setDeletedImageIds] = useState<Set<string>>(() => new Set());
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  const visibleImages = images.filter((image) => !deletedImageIds.has(image.id));
  const clampedSelectedIndex = selectedIndex === null || visibleImages.length === 0
    ? null
    : Math.min(selectedIndex, visibleImages.length - 1);

  if (visibleImages.length === 0) return null;

  async function deleteImage(image: ObservationImageData) {
    setDeletingImageId(image.id);
    try {
      const response = await fetch(observationImagePath(rdId, noteId, image.id), {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, "Failed to remove image."));
      }
      const nextImages = visibleImages.filter((item) => item.id !== image.id);
      setDeletedImageIds((prev) => new Set(prev).add(image.id));
      toast.success("Image removed");
      onChanged?.(nextImages);
    } finally {
      setDeletingImageId(null);
    }
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-sand-500">
        <ImageIcon className="h-3.5 w-3.5" />
        <span>
          {heading} · {visibleImages.length}
        </span>
      </div>

      <div className="flex flex-wrap gap-4">
        {visibleImages.map((image, index) => (
          <div
            key={image.id}
            className="group relative w-[88px] rounded-[10px] border border-sand-200 bg-card"
          >
            <button
              type="button"
              onClick={() => setSelectedIndex(index)}
              className="block w-[88px] overflow-hidden rounded-[10px] text-left"
              aria-label={`View observation attachment ${index + 1} of ${visibleImages.length}`}
            >
              <img
                src={image.url}
                alt={`Observation attachment ${index + 1} of ${visibleImages.length}`}
                className="h-[88px] w-[88px] object-cover object-center transition-transform group-hover:scale-[1.02]"
              />
              <div className="px-2 py-1.5 text-[11px] text-sand-500">
                Image {index + 1} · {formatObservationImageByteSize(image.byteSize)}
              </div>
            </button>
            {editable && (
              <ObservationImageDeleteDialog
                title="Remove this image?"
                description="This removes the image from the observation. The observation text stays in place."
                actionLabel="Remove image"
                onConfirm={() => deleteImage(image)}
                trigger={
                  <button
                    type="button"
                    disabled={deletingImageId === image.id}
                    className="absolute -right-2 -top-2 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-bark text-white shadow-sm ring-2 ring-white transition-colors hover:bg-error disabled:opacity-50"
                    aria-label={`Remove observation attachment ${index + 1}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                }
              />
            )}
          </div>
        ))}
      </div>

      <ObservationImageCarouselDialog
        key={clampedSelectedIndex ?? "closed"}
        images={visibleImages}
        initialIndex={clampedSelectedIndex ?? 0}
        open={clampedSelectedIndex !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedIndex(null);
        }}
      />
    </div>
  );
}
